import React, { useEffect } from 'react';

import { useConfiguration } from '../../../contexts/ConfigurationContext';

interface TabSandboxProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
	anchorElem?: HTMLElement | null;
	wrapperRef?: React.Ref<HTMLDivElement>;
}

const TabSandbox = ({ children, anchorElem, wrapperRef, ...rest }: TabSandboxProps) => {
	const { setOpenSettingMenu } = useConfiguration();
	const tabSandboxRef = React.useRef<HTMLDivElement | null>(null);

	const tabOutAction = () => {
		setOpenSettingMenu(undefined);
		anchorElem?.focus();
	};

	const handleKeyDown = (e: any) => {
		if (e.key.includes('Arrow')) {
			e.stopPropagation();
			e.preventDefault();
			return;
		}

		if (e.key === 'Escape') {
			tabOutAction();
			return;
		}
	};

	const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
		if (tabSandboxRef.current) {
			const focusableElements = tabSandboxRef.current.querySelectorAll<HTMLElement>(
				'a[href]:not([tabindex="-1"]), button:not([tabindex="-1"]), textarea:not([tabindex="-1"]), input[type="text"]:not([tabindex="-1"]), input[type="radio"]:not([tabindex="-1"]), input[type="checkbox"]:not([tabindex="-1"]), select:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
			);
			if (focusableElements.length === 0) {
				tabOutAction();
				return;
			}

			if (e.relatedTarget) {
				if (!tabSandboxRef.current.contains(e.relatedTarget as Node)) {
					tabOutAction();
					return;
				}
			} else {
				// Focus moved outside the document (e.g., browser address bar)
				tabOutAction();
			}
		}
	};

	useEffect(() => {
		// Focus on the first focusable element when the component mounts
		if (tabSandboxRef.current) {
			const focusableElements = tabSandboxRef.current.querySelectorAll<HTMLElement>(
				'a[href]:not([tabindex="-1"]), button:not([tabindex="-1"]), textarea:not([tabindex="-1"]), input[type="text"]:not([tabindex="-1"]), input[type="radio"]:not([tabindex="-1"]), input[type="checkbox"]:not([tabindex="-1"]), select:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
			);
			if (focusableElements.length > 0) {
				focusableElements[0].focus();
			}
		}
	}, []);

	return <div
		{...rest}
		ref={r => {
			tabSandboxRef.current = r;
			if (typeof wrapperRef === 'function') {
				wrapperRef(r);
			} else if (wrapperRef) {
				(wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = r;
			}
		}}
		onKeyDown={handleKeyDown}
		onBlur={onBlur}
	>
		{children}
	</div>;
};

export default TabSandbox;